import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import type { CartItem } from '../context/cart';
import { formatCNPJ } from '../functions';

const BRAND: [number, number, number] = [142, 81, 255];
const BAR_H = 15; // altura da barra roxa em mm — ajuste aqui

interface Props {
	pdf: {
		title: string;
		isPurchaseOrder: boolean;
		generatedAt: string;
		emitente?: {
			fantasia?: string;
			cnpj?: string;
		};
	};
	items: CartItem[];
}

export function generateOrderPDF({ pdf, items }: Props) {
	const doc = new jsPDF('p', 'mm', 'a4') as jsPDF & {
		lastAutoTable?: { finalY: number };
	};
	const pageHeight = doc.internal.pageSize.getHeight();
	const pageWidth = doc.internal.pageSize.getWidth();

	const emitenteInfo = () => {
		let y = Math.round(BAR_H * 0.4);
		doc.setTextColor(255, 255, 255);
		if (pdf.emitente?.fantasia) {
			doc.setFontSize(9);
			doc.setFont('helvetica', 'bold');
			doc.text(pdf.emitente.fantasia.toUpperCase(), pageWidth - 5, y, {
				align: 'right',
			});
			y += 3;
		}
		doc.setFontSize(7);
		doc.setFont('helvetica', 'normal');
		doc.setTextColor(220, 200, 255);
		if (pdf.emitente?.cnpj) {
			doc.text(`CNPJ: ${formatCNPJ(pdf.emitente.cnpj)}`, pageWidth - 5, y, {
				align: 'right',
			});
			y += 3;
		}
		doc.text(`Gerado em: ${pdf.generatedAt}`, pageWidth - 5, y, {
			align: 'right',
		});
	};

	const header = () => {
		doc.setFillColor(...BRAND);
		doc.rect(0, 0, pageWidth, BAR_H, 'F');

		doc.setTextColor(255, 255, 255);
		doc.setFont('helvetica', 'bold');
		doc.setFontSize(13);
		doc.text(pdf.title.toUpperCase(), 5, Math.round(BAR_H * 0.65));

		emitenteInfo();
	};

	header();

	const fmt = (v: number) =>
		v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

	const head = pdf.isPurchaseOrder
		? [['#', 'Produto / EAN / Cód.', 'Qtd', 'Preço Unit.', 'Total']]
		: [['#', 'Produto / EAN', 'Qtd', 'Preço', 'Subtotal']];

	const body = items.map((item, i) => {
		const qty = `${item.isWeight ? item.quantity.toFixed(2).replace('.', ',') : item.quantity} ${item.unit ?? 'un'}`;
		const name = `${item.name}\nCód: ${item.id}  |  EAN: ${item.barcode}`;

		if (pdf.isPurchaseOrder) {
			return [String(i + 1), name, qty, '', ''];
		}
		return [
			String(i + 1),
			name,
			qty,
			fmt(item.price),
			fmt(item.price * item.quantity),
		];
	});

	const totalPrice = items.reduce(
		(acc, item) => acc + item.price * item.quantity,
		0,
	);

	const foot = pdf.isPurchaseOrder
		? []
		: [['', '', '', 'Total', fmt(totalPrice)]];

	autoTable(doc, {
		head,
		body,
		foot,
		startY: BAR_H + 8,
		margin: { top: 5, left: 5, right: 5, bottom: 5 },
		styles: {
			fontSize: 8,
			cellPadding: [2, 2],
		},
		showFoot: 'lastPage',
		showHead: 'firstPage',
		headStyles: {
			fillColor: BRAND,
			textColor: [255, 255, 255],
			fontStyle: 'bold',
		},
		footStyles: {
			fillColor: [230, 230, 230],
			textColor: [0, 0, 0],
			fontStyle: 'bold',
			halign: 'right',
		},
		columnStyles: pdf.isPurchaseOrder
			? {
					0: { cellWidth: 8, halign: 'center' },
					1: { cellWidth: 'auto' },
					2: { cellWidth: 20, halign: 'center' },
					3: { cellWidth: 28, halign: 'right' },
					4: { cellWidth: 28, halign: 'right' },
				}
			: {
					0: { cellWidth: 8, halign: 'center' },
					1: { cellWidth: 'auto' },
					2: { cellWidth: 22, halign: 'center' },
					3: { cellWidth: 28, halign: 'right' },
					4: { cellWidth: 28, halign: 'right' },
				},
		didDrawCell: pdf.isPurchaseOrder
			? (data) => {
					if (
						data.section === 'body' &&
						(data.column.index === 3 || data.column.index === 4)
					) {
						const { x, y, width, height } = data.cell;
						doc.setDrawColor(160, 160, 160);
						doc.setLineWidth(0.3);
						doc.line(x + 2, y + height - 2.5, x + width - 2, y + height - 2.5);
					}
				}
			: undefined,
	});

	if (pdf.isPurchaseOrder) {
		const noteY = (doc.lastAutoTable?.finalY ?? 0) + 6;
		if (pageHeight - noteY > 15) {
			doc.setFontSize(7);
			doc.setFont('helvetica', 'italic');
			doc.setTextColor(146, 64, 14);
			doc.text(
				'Preencha os campos de Preco Unit. e Total e devolva para orcamento.',
				5,
				noteY,
			);
		}
	}

	doc.setFontSize(7);
	doc.setFont('helvetica', 'normal');
	doc.setTextColor(120, 120, 120);
	doc.text('Powered by', 5, pageHeight - 5);
	doc.setFont('helvetica', 'bolditalic');
	doc.setTextColor(...BRAND);
	doc.textWithLink('HubMaster', 18.2, pageHeight - 5, {
		url: 'https://www.hubmaster.com.br',
	});

	const filename = `${pdf.title.replace(/\s+/g, '-')}-${new Date()
		.toLocaleDateString('pt-BR')
		.replace(/\//g, '-')}.pdf`;

	return {
		download: () => {
			doc.save(filename);
		},
		share: async () => {
			const blob = doc.output('blob');
			const file = new File([blob], filename, { type: 'application/pdf' });

			if (navigator.canShare?.({ files: [file] })) {
				try {
					await navigator.share({ files: [file], title: pdf.title });
					return;
				} catch {
					// user cancelled or not supported → open in tab
				}
			}

			const url = URL.createObjectURL(blob);
			window.open(url, '_blank');
			setTimeout(() => URL.revokeObjectURL(url), 60_000);
		},
	};
}
